// Everything missioncontrol needs to talk to the pathfinder service: the base
// URL, the nginx key header and the error-body decoder. One place, so the
// /path/find and /path/simulate callers cannot drift apart.

export const PATHFINDER_DEFAULT_BASE_URL = 'https://pathfinder.rogoag.com';

export const PATHFINDER_FIND_PATH = '/path/find';
export const PATHFINDER_SIMULATE_PATH = '/path/simulate';

/**
 * The pathfinder service root, with no route suffix.
 *
 * Pathfinder runs on our own box behind nginx. NOTE that Crow only serves
 * explicit routes, so a host-only URL 404s; VITE_PATHFINDER_URL historically
 * carried '/path/find' (pathfinder's find route), which is stripped here and
 * re-added by pathfinderFindUrl().
 *
 * No ready rollback: the old Lambda Function URL
 *   https://ctmsq2f5rao27stnarbabsyjb40kehpv.lambda-url.us-east-2.on.aws
 * still exists but runs pathfinder code from 2026-05-20 (its CI deploy never
 * succeeded and is now manual-only). Redeploy it first — pathfinder's
 * deploy.yml, which needs an AWS_REGION repo variable — then point
 * VITE_PATHFINDER_URL at it.
 *
 * Local dev / robots: VITE_PATHFINDER_URL=http://localhost:8082/path/find
 */
export function pathfinderBaseUrl(): string {
  const configured = (import.meta.env.VITE_PATHFINDER_URL as string | undefined)?.trim();
  const trimmed = (configured || `${PATHFINDER_DEFAULT_BASE_URL}${PATHFINDER_FIND_PATH}`).replace(/\/+$/, '');

  return trimmed.endsWith(PATHFINDER_FIND_PATH) ? trimmed.slice(0, -PATHFINDER_FIND_PATH.length) : trimmed;
}

export function pathfinderFindUrl(): string {
  return `${pathfinderBaseUrl()}${PATHFINDER_FIND_PATH}`;
}

export function pathfinderSimulateUrl(): string {
  return `${pathfinderBaseUrl()}${PATHFINDER_SIMULATE_PATH}`;
}

/**
 * nginx in front of the on-prem box gates pathfinder's routes on this header.
 * Unset for local/Lambda, where it is simply ignored.
 */
export function pathfinderHeaders(): Record<string, string> | undefined {
  const key = import.meta.env.VITE_PATHFINDER_KEY as string | undefined;

  return key ? { 'X-Pathfinder-Key': key } : undefined;
}

/**
 * Human-readable detail of a failed pathfinder request.
 *
 * In debug modes the request asks for `responseType: 'arraybuffer'` because the
 * response is sliced byte-wise. Axios applies that responseType to ERROR
 * responses too, so a 4xx/5xx body arrives as an ArrayBuffer — `response.data.error`
 * is undefined on it, and reading it directly surfaces the useless
 * "[object ArrayBuffer]". Decode the bytes (the server sends JSON like
 * {"error":"No waypoints provided"}) before reading `.message` / `.error`.
 */
export function extractPathfinderErrorDetail(error: unknown, elapsedSec: number): string {
  if (!(error instanceof Error)) {
    return 'Unknown error';
  }

  const response = (error as { response?: { data?: unknown } }).response;

  // axios' ERR_NETWORK with no response: the browser never got an HTTP
  // response at all (blackholed/refused connection, DNS, offline, or a
  // CORS-masked proxy error). The bare "Network Error" reads like a solver
  // failure, so say what happened. The elapsed time tells support which
  // case it was: ~0s blocked/offline, ~21s Windows TCP connect timeout
  // (e.g. a router that doesn't NAT-hairpin to the on-prem box), minutes =
  // proxy timeout.
  if (!response && (error as { code?: unknown }).code === 'ERR_NETWORK') {
    return `Can't reach the Smart Path server (no response after ${elapsedSec}s). Check your connection or try another network (VPN / hotspot).`;
  }

  if (!response || response.data === undefined || response.data === null) {
    return error.message;
  }

  let data: unknown = response.data;

  // Detect any BufferSource (ArrayBuffer or a typed-array/DataView view of
  // one). `instanceof ArrayBuffer` is not realm-safe — bytes that crossed a
  // realm boundary (Worker, jsdom test runner) fail it — so fall back to the
  // structural tag check. `ArrayBuffer.isView` is already realm-safe.
  if (ArrayBuffer.isView(data) || Object.prototype.toString.call(data) === '[object ArrayBuffer]') {
    const byteLength = (data as ArrayBuffer | ArrayBufferView).byteLength;

    // Empty body carries no detail — fall back to axios' own message
    // (e.g. "Request failed with status code 502").
    if (byteLength === 0) {
      return error.message;
    }

    const text = new TextDecoder().decode(data as ArrayBuffer | ArrayBufferView);

    try {
      data = JSON.parse(text);
    } catch {
      // Non-JSON body (plain-text error page) — surface the text as-is.
      return text;
    }
  }

  if (typeof data === 'string') {
    return data.length > 0 ? data : error.message;
  }

  if (typeof data === 'object' && data !== null) {
    const { message, error: errorField } = data as { message?: unknown; error?: unknown };
    const detail = typeof message === 'string' && message.length > 0 ? message : errorField;

    if (typeof detail === 'string' && detail.length > 0) {
      return detail;
    }
  }

  return error.message;
}
